import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Flex,
  HStack,
  VStack,
  Text,
  Image,
  Button,
  IconButton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  Divider,
  useDisclosure,
  useColorModeValue,
} from "@chakra-ui/react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { HamburgerIcon } from "@chakra-ui/icons";
// ✅ Use your Traversal logo here

const toFrenchPath = (path) => {
  if (!path || path === "/") return "/fr";
  if (path === "/en") return "/fr"; // optional safety
  if (path === "/fr") return "/fr";

  // already french?
  if (path.endsWith("-fr")) return path;

  // add suffix
  return `${path}-fr`;
};

const toEnglishPath = (path) => {
  if (!path || path === "/") return "/";
  if (path === "/fr") return "/";
  if (path === "/en") return "/";

  // remove suffix
  if (path.endsWith("-fr")) return path.replace(/-fr$/, "");

  // already english
  return path;
};

const isFrenchPath = (path) => path === "/fr" || path.endsWith("-fr");

export function LanguageToggle({ compact = false, onDone }) {
  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();

  const go = (nextLang) => {
    const current = pathname || "/";

    const nextPath =
      nextLang === "fr" ? toFrenchPath(current) : toEnglishPath(current);

    // keep query params & hash
    navigate(`${nextPath}${search || ""}${hash || ""}`);
    onDone?.();
  };

  const currentlyFR = isFrenchPath(pathname || "/");

  return (
    <button onClick={() => go(currentlyFR ? "en" : "fr")}>
      {compact ? (currentlyFR ? "EN" : "FR") : currentlyFR ? "English" : "Français"}
    </button>
  );
}

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // -------------------------
  // EN/FR detection via URL prefix
  // -------------------------
  const lang = location.pathname.startsWith("/fr") ? "fr" : "en";
  const base = lang === "fr" ? "/fr" : "/en";

  // Active path without language prefix (for highlighting)
  const localPath = location.pathname.replace(/^\/(en|fr)/, "") || "/";

  const labels = useMemo(
    () => ({
      en: {
        project: "TRAVERSAL",
        tagline: "Training and Research in Autonomous Vehicles",
        home: "Home",
        team: "Team",
        program: "Program",
        seminars: "Seminars",
        publications: "Publications",
        news: "News",
        contact: "Contact",
        menu: "Menu",
      },
      fr: {
        project: "Projet de recherche TRAVERSAL",
        tagline: "IA fiable • Mobilité • Intelligence en périphérie",
        home: "Accueil",
        team: "Équipe",
        program: "Programme",
        seminars: "Séminaires",
        publications: "Publications",
        news: "Actualités",
        contact: "Contact",
        menu: "Menu",
      },
    }),
    []
  );

  const t = labels[lang];

  const links = useMemo(
    () => [
      { to: `/`, key: "/", label: t.home },
      { to: `/about-us`, key: "/about-us", label: t.team },
      { to: `/program`, key: "/program", label: t.program },
      { to: `/seminars`, key: "/seminars", label: t.seminars },
      { to: `/publications`, key: "/publications", label: t.publications },
      { to: `/news`, key: "/news", label: t.news },
    ],
    [base, t]
  );

  // -------------------------
  // Palette (different identity)
  // -------------------------
  const pageBg = useColorModeValue("white", "#0B1220");
  const headerBg = useColorModeValue("#e2e8f0c4", "#0B1220");
  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  const text = useColorModeValue("#38549a", "#E5E7EB");
  const muted = useColorModeValue("#475569", "#AAB3C2");
  const accent = "#06B6D4";
  const accent2 = "#22D3EE";

  const NavItem = ({ to, label, isActive, onClick }) => (
    <Button
      as={Link}
      to={to}
      onClick={onClick}
      variant="ghost"
      size="sm"
      px={3}
      py={2}
      borderRadius="999px"
      fontWeight="800"
      color={isActive ? text : muted}
      _hover={{
        bg: useColorModeValue("rgba(15,23,42,0.04)", "rgba(148,163,184,0.10)"),
        color: text,
      }}
      position="relative"
    >
      {label}
      <Box
        position="absolute"
        left="18%"
        right="18%"
        bottom="-8px"
        height="2px"
        borderRadius="999px"
        bg={accent}
        opacity={isActive ? 1 : 0}
        transform={isActive ? "scaleX(1)" : "scaleX(0.65)"}
        transition="all 180ms ease"
      />
    </Button>
  );

  const LanguageToggle = ({ compact = false, onDone }) => {
    const navigate = useNavigate();
    const { pathname, search, hash } = useLocation();

    const go = (nextLang) => {
      const current = pathname || "/";

      let nextPath;

      if (nextLang === "fr") {
        // English -> French
        if (current === "/") {
          nextPath = "/fr";
        } else if (current.endsWith("-fr")) {
          nextPath = current; // already French
        } else {
          nextPath = `${current}-fr`;
        }
      } else {
        // French -> English
        if (current === "/fr") {
          nextPath = "/";
        } else if (current.endsWith("-fr")) {
          nextPath = current.replace(/-fr$/, "");
        } else {
          nextPath = current; // already English
        }
      }

      navigate(`${nextPath}${search || ""}${hash || ""}`);
      onDone?.();
    };

    return (
      <HStack
        spacing={1}
        border="1px solid"
        borderColor={border}
        borderRadius="999px"
        p="2px"
        bg={useColorModeValue("rgba(255,255,255,0.65)", "rgba(255,255,255,0.04)")}
      >
        <Button
          size={compact ? "xs" : "sm"}
          borderRadius="999px"
          variant={lang === "en" ? "solid" : "ghost"}
          bg={lang === "en" ? accent : "transparent"}
          color={lang === "en" ? "#001018" : muted}
          fontWeight="900"
          _hover={{ filter: lang === "en" ? "brightness(0.95)" : "none", bg: lang === "en" ? accent : "transparent" }}
          onClick={() => go("en")}
        >
          EN
        </Button>
        <Button
          size={compact ? "xs" : "sm"}
          borderRadius="999px"
          variant={lang === "fr" ? "solid" : "ghost"}
          bg={lang === "fr" ? accent : "transparent"}
          color={lang === "fr" ? "#001018" : muted}
          fontWeight="900"
          _hover={{ filter: lang === "fr" ? "brightness(0.95)" : "none", bg: lang === "fr" ? accent : "transparent" }}
          onClick={() => go("fr")}
        >
          FR
        </Button>
      </HStack>
    );
  };

  return (
    <>
    <Box
      position="sticky"
      top={0}
      zIndex={1000}
      bg={headerBg}
      backdropFilter="blur(14px)"
      borderBottom="1px solid"
      borderColor={border}
    >
      {/* Accent line */}
      <Box h="3px" bg={`linear-gradient(90deg, ${accent} 0%, ${accent2} 40%, rgba(34,211,238,0) 100%)`} />

      {/* Tier 1: identity */}
      <Container maxW="1200px" py={{ base: 2.5, md: 3.5 }}>
        <Flex align="center" justify="space-between" gap={3}>
          <HStack spacing={{ base: 3, md: 4 }} align="center" minW="fit-content">
            <Box
              border="1px solid"
              borderColor={useColorModeValue("rgba(2,6,23,0.08)", "rgba(148,163,184,0.18)")}
              borderRadius="16px"
              bg={useColorModeValue("rgba(255,255,255,0.60)", "rgba(255,255,255,0.04)")}
              px={{ base: 2, md: 2.5 }}
              py={{ base: 1.5, md: 2 }}
            >
              {/* ✅ Smaller logo + no cropping */}
              <Image
                src="traversal_logo.png"
                alt="TRAVERSAL"
                // Smaller than before:
                maxW={{ base: "110px", md: "120px", lg: "120px" }}
                w="100%"
                h="auto"
                objectFit="contain"
                objectPosition="left center"
                draggable={false}
              />
            </Box>

            <Box display={{ base: "none", md: "block" }} lineHeight={1.15}>
              <Text fontSize="sm" fontWeight="900" color={text} letterSpacing="0.02em">
                {t.project}
              </Text>
              <Text fontSize="sm" color={muted} fontWeight="600">
                {t.tagline}
              </Text>
            </Box>
          </HStack>

          <HStack spacing={2}>
            
            <Button
              as={Link}
              to={`/contact-us`}
              size="sm"
              display={{ base: "none", md: "inline-flex" }}
              bg={accent}
              color="#001018"
              fontWeight="900"
              borderRadius="999px"
              _hover={{ filter: "brightness(0.95)" }}
            >
              {t.contact}
            </Button>

            <Box display={{ base: "none", md: "block" }}>
              <LanguageToggle />
            </Box>


            <IconButton
              aria-label="Open menu"
              icon={<HamburgerIcon />}
              variant="ghost"
              display={{ base: "inline-flex", md: "none" }}
              onClick={onOpen}
            />
          </HStack>
        </Flex>
      </Container>

      {/* Tier 2: nav */}
      <Box bg={useColorModeValue("rgba(15,23,42,0.02)", "rgba(148,163,184,0.06)")}>
        <Container maxW="1200px" py={2}>
          <Flex align="center" justify="space-between">
            <HStack spacing={1} display={{ base: "none", md: "flex" }}>
              {links.map((l) => (
                <NavItem key={l.to} to={l.to} label={l.label} isActive={localPath === l.key} />
              ))}
            </HStack>

            <Text
              display={{ base: "none", lg: "block" }}
              fontSize="sm"
              color={muted}
              fontWeight="700"
              letterSpacing="0.01em"
            >
              {lang === "fr" ? "Recherche ouverte • Publications • Séminaires" : "Open research • Publications • Seminars"}
            </Text>
          </Flex>
        </Container>
      </Box>

      {/* Mobile Drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xs">
        <DrawerOverlay />
        <DrawerContent bg={pageBg}>
          <DrawerCloseButton />
          <DrawerHeader>
            <VStack align="start" spacing={2}>
              <Text fontWeight="900" color={text}>
                {t.menu}
              </Text>
              <LanguageToggle compact onDone={onClose} />
            </VStack>
          </DrawerHeader>

          <DrawerBody>
            <VStack align="stretch" spacing={1}>
              {links.map((l) => (
                <Button
                  key={l.to}
                  as={Link}
                  to={l.to}
                  onClick={onClose}
                  justifyContent="flex-start"
                  variant="ghost"
                  fontWeight="800"
                  color={localPath === l.key ? text : muted}
                  _hover={{
                    bg: useColorModeValue("rgba(15,23,42,0.04)", "rgba(148,163,184,0.10)"),
                  }}
                >
                  {l.label}
                </Button>
              ))}

              <Divider my={3} borderColor={border} />

              <Button
                as={Link}
                to={`/contact-us`}
                onClick={onClose}
                bg={accent}
                color="#001018"
                fontWeight="900"
                borderRadius="999px"
                _hover={{ filter: "brightness(0.95)" }}
              >
                {t.contact}
              </Button>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>


        {/* Official Project Title Strip */}
        <Box
            bg={useColorModeValue("#f1f5f9", "#0F172A")}
            borderBottom="1px solid"
            borderColor={border}
            >
            <Container maxW="1200px" py={{ base: 2, md: 3 }}>
                <Text
                fontSize={{ base: "xs", md: "sm" }}
                fontWeight="900"
                color={text}
                textAlign="center"
                letterSpacing="0.03em"
                >
                TRAINING AND RESEARCH IN AUTONOMOUS VEHICLES FOR RELIABLE SERVICES IN
                THE AIR AND ON LAND (TRAVERSAL)
                </Text>
            </Container>
        </Box>

    </Box>

        


    </>
  );
}
